import React from "react";
import { useCase } from "../../../../contexts/CaseContext";
import { Subheading } from "../../../../design/heading";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../design/table";
import CreateUserButton from "./components/CreateParentButton";
import DeleteParentButton from "./DeleteParentButton";
import CustodyStatusBadge from "../../../../components/CustodyStatusBadge";
import { Avatar } from "../../../../design/avatar";
import { Text } from "../../../../design/text";

interface Props {
  caseId: string;
}

const formatAddress = (address: any): string | null => {
  if (!address) return null;
  const parts = [
    address.streetAddress,
    address.streetAddress2,
    [address.city, address.state].filter(Boolean).join(", "),
    address.zipCode,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : address.formattedAddress || null;
};

const CaseDetailUserList: React.FC<Props> = ({ caseId }) => {
  const { parents } = useCase();
  return (
    <>
      <div className="flex flex-wrap items-center justify-between w-full">
        <Subheading>Parents / Guardians</Subheading>
        <div className="flex gap-4">
          <CreateUserButton caseId={caseId} />
        </div>
      </div>
      {parents.length === 0 && (
        <div className="py-8 text-center">
          <p className="text-sm text-zinc-500 dark:text-zinc-400">No parents or guardians have been added yet.</p>
        </div>
      )}

      {parents.length > 0 && (
        <Table className="mt-4 [--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
          <TableHead>
            <TableRow>
              <TableHeader>Name</TableHeader>
              <TableHeader>Phone Number</TableHeader>
              <TableHeader>Email</TableHeader>
              <TableHeader>Address</TableHeader>
              <TableHeader>Relationship</TableHeader>
              <TableHeader>Custody</TableHeader>
              <TableHeader>Actions</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {parents.map((parent) => {
              const addressText = formatAddress(parent.address);
              return (
                <TableRow
                  key={parent.id}
                  title={`Parent ${parent.id}`}
                  href={`/parent/${parent.id}`}
                >
                  <TableCell>
                    <Avatar
                      src={parent.avatarUrl}
                      className="mr-2 align-middle size-6"
                      initials={`${parent.firstName?.[0] ?? ""}${parent.lastName?.[0] ?? ""}`}
                    />
                    {parent.firstName} {parent.lastName}
                  </TableCell>
                  <TableCell>{parent.phoneNumber}</TableCell>
                  <TableCell>{parent.email}</TableCell>
                  <TableCell>
                    {addressText ? (
                      <Text className="text-sm text-zinc-700 dark:text-zinc-300 max-w-xs truncate" title={addressText}>
                        {addressText}
                      </Text>
                    ) : (
                      <Text className="text-sm text-zinc-400">—</Text>
                    )}
                  </TableCell>
                  <TableCell>{parent.relationshipToChild}</TableCell>
                  <TableCell>
                    <CustodyStatusBadge status={parent.custodyStatus} />
                  </TableCell>
                  <TableCell>
                    <DeleteParentButton parent={parent} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </>
  );
};

export default CaseDetailUserList;
